import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api/client";
import type { CatalogCollection, PriceQuote } from "../lib/api/types";
import { tenantPath, useCatalogueRouteMode, useTenantSlug } from "../lib/tenant";
import { useVisualizer, type DoorObject } from "../lib/state/visualizerStore";
import { configuredDoors, effectiveSize, resolveDoorNames } from "../lib/state/selectors";
import { sizeDims, sizeLabel } from "../lib/geometry/doorSize";
import { useBranding } from "../lib/state/brandingContext";
import { AppShell, card } from "../components/AppShell";
import { Button } from "../components/Button";
import { CheckIcon } from "../components/icons";
import { PriceLine } from "../components/PriceLine";
import { AddressAutocomplete } from "../components/AddressAutocomplete";
import { dollars, dollarsCents } from "../lib/format";
import { formatUsPhone, isPhoneComplete, isValidEmail } from "../lib/validation";

type DoorQuote = { quote: PriceQuote | null; loading: boolean };

export function SchedulePage() {
  const tenantSlug = useTenantSlug();
  const navigate = useNavigate();
  const doc = useVisualizer((s) => s.document);
  const render = useVisualizer((s) => s.render);
  const catalog = useVisualizer((s) => s.catalog);
  const setCatalog = useVisualizer((s) => s.setCatalog);
  const selectDoor = useVisualizer((s) => s.selectDoor);
  const setMode = useVisualizer((s) => s.setMode);
  const resetAll = useVisualizer((s) => s.resetAll);
  const { branding } = useBranding();
  // Showroom is price-free: the server sends pricingEnabled:false, so we skip every price fetch + surface.
  const pricingEnabled = branding?.pricingEnabled ?? true;

  // The inquiry-consent disclosure. ONE definition shared by the rendered line and the submitted
  // consentText, so what the lead stores is literally what the homeowner saw — the verbatim match
  // is what makes it an audit record rather than copy.
  const texts = branding?.texts ?? null;
  const consentSentence =
    texts?.consentLine
    ?? (branding?.name
      ? `By requesting your quote you agree ${branding.name} may contact you about it by phone, text or email. No spam, no obligation.`
      : "By requesting your quote you agree we may contact you about it by phone, text or email. No spam, no obligation.");
  const catalogueMode = useCatalogueRouteMode();
  const display = render.integratedUrl ?? render.compositeUrl ?? doc.photoUrl;
  // Postcard QR session — the quote goes to the postcard-scoped endpoint, which writes the
  // dealer's KNOWN lead (no name/address form; identity is proven by the code).
  const postcard = doc.postcard ?? null;

  // The demo/manufacturer scopes have no dealer to quote from — this page doesn't exist for them.
  // Bounce back to the editor (deep links included); the server rejects demo lead posts anyway.
  const leadCaptureDisabled = branding != null && !branding.leadCaptureEnabled;
  useEffect(() => {
    if (leadCaptureDisabled) navigate(tenantPath(tenantSlug, "visualize"), { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadCaptureDisabled]);
  // Address prefills from the session's source address ("Use your address" flow) — the customer
  // already told us where they live; the field stays fully editable.
  const [form, setForm] = useState({ fullName: "", address: doc.sourceAddress ?? "", phone: "+1 ", email: "" });
  const [errors, setErrors] = useState<{ fullName?: string; address?: string; phone?: string; email?: string }>({});
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The catalogue is in-memory only (the store persists just `document`), so on a hard refresh of
  // /schedule we re-fetch it and match the document's collection — otherwise door names + prices are blank.
  const [collection, setCollection] = useState<CatalogCollection | null>(catalog.collection);
  useEffect(() => {
    if (!doc.photoUrl) { navigate(tenantPath(tenantSlug)); return; }
    if (catalog.collection) { setCollection(catalog.collection); return; }
    if (!doc.collectionId) return;
    let cancelled = false;
    api.getCollections(tenantSlug, { mode: catalogueMode })
      .then((cols) => {
        if (cancelled) return;
        const c = cols.find((x) => x.id === doc.collectionId) ?? null;
        setCollection(c);
        setCatalog({ collections: cols, ...(c ? { collection: c } : {}) });
      })
      .catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantSlug, catalogueMode]);

  // Responsive: stack the summary above the form on narrow screens (inline styles → track width in state).
  const [vw, setVw] = useState(typeof window !== "undefined" ? window.innerWidth : 1024);
  useEffect(() => {
    const onResize = () => setVw(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  const stacked = vw < 720;

  // Every door the homeowner configured (design + colour). sizeOverride persists, so sizes survive refresh.
  const doors = useMemo(() => configuredDoors({ document: doc }), [doc]);

  // Price each door against the dealer's rules — same param mapping the editor uses. Re-runs only when a
  // door's selection or size changes (serialized key) or the collection finishes hydrating.
  const [quotes, setQuotes] = useState<Record<string, DoorQuote>>({});
  const priceKey = doors
    .map((d) => `${d.id}:${d.selection.designId}:${d.selection.colorId}:${d.selection.windowOptionId}:${d.selection.hardwareId}:${effectiveSize(d)}`)
    .join("|");
  useEffect(() => {
    if (!pricingEnabled || !collection || doors.length === 0) return;
    let cancelled = false;
    setQuotes((q) => {
      const next = { ...q };
      for (const d of doors) next[d.id] = { quote: next[d.id]?.quote ?? null, loading: true };
      return next;
    });
    Promise.all(
      doors.map((d) => {
        const sel = d.selection;
        const color = collection.colors.find((c) => c.id === sel.colorId);
        const win = collection.windows.find((w) => w.id === sel.windowOptionId);
        return api
          .price(tenantSlug, {
            collectionId: collection.id,
            designId: sel.designId ?? undefined,
            colorId: sel.colorId ?? undefined,
            colorGroup: color?.family ?? undefined,
            windowOptionId: sel.windowOptionId ?? undefined,
            windowGroup: win?.placement ?? undefined,
            hardwareId: sel.hardwareId ?? undefined,
            constructionId: sel.constructionId ?? undefined,
            isSingle: effectiveSize(d) === "single",
          })
          .then((quote) => ({ id: d.id, quote }))
          .catch(() => ({ id: d.id, quote: null as PriceQuote | null }));
      }),
    ).then((results) => {
      if (cancelled) return;
      setQuotes((q) => {
        const next = { ...q };
        for (const r of results) next[r.id] = { quote: r.quote, loading: false };
        return next;
      });
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantSlug, collection?.id, priceKey, pricingEnabled]);

  // Combined "starting from" total — only once every door has a resolved quote.
  const allLoaded = doors.length > 0 && doors.every((d) => quotes[d.id] && !quotes[d.id].loading);
  const totals = useMemo(() => {
    if (!allLoaded) return null;
    let price = 0, monthly = 0, anyPrice = false, anyMonthly = false;
    for (const d of doors) {
      const q = quotes[d.id]?.quote;
      if (!q) continue;
      const isSingle = effectiveSize(d) === "single";
      const showPromo = !isSingle && q.promo16x7Cents != null;
      price += showPromo ? q.promo16x7Cents! : (isSingle ? q.price8x7Cents : q.price16x7Cents);
      anyPrice = true;
      const m = isSingle ? q.financeMonthly8x7Cents : q.financeMonthly16x7Cents;
      if (m != null) { monthly += m; anyMonthly = true; }
    }
    return { price, monthly, anyPrice, anyMonthly };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allLoaded, priceKey, quotes]);

  function update(key: keyof typeof form, value: string) {
    setForm((s) => ({ ...s, [key]: value }));
    // Clear a field's error as soon as the customer edits it.
    setErrors((er) => (er[key] ? { ...er, [key]: undefined } : er));
  }

  // Name + address required; phone required + complete (10-digit US); email OPTIONAL but valid if
  // entered. Postcard QR sessions already know who and where the homeowner is (the code proves it),
  // so only the contact preference is collected there.
  function validate() {
    const e: typeof errors = {};
    if (!postcard) {
      if (!form.fullName.trim()) e.fullName = "Please enter your name.";
      if (!form.address.trim()) e.address = "Please enter your address.";
    }
    if (!isPhoneComplete(form.phone)) e.phone = "Enter a 10-digit US phone number.";
    if (form.email.trim() && !isValidEmail(form.email)) e.email = "Enter a valid email address.";
    return e;
  }

  // Jump back into the visualizer with this door pre-selected. The store's ui slice persists across the
  // client-side navigation (no reload), so VisualizePage opens focused on the chosen door.
  function editDoor(id: string) {
    selectDoor(id);
    setMode("edit");
    navigate(tenantPath(tenantSlug, "visualize"));
  }

  function doorChips(d: DoorObject) {
    const sel = d.selection;
    const design = collection?.designs.find((x) => x.id === sel.designId);
    const color = collection?.colors.find((x) => x.id === sel.colorId);
    const win = sel.windowOptionId ? collection?.windows.find((x) => x.id === sel.windowOptionId) : undefined;
    const hardware = sel.hardwareId ? collection?.hardware.find((x) => x.id === sel.hardwareId) : undefined;
    // The chosen insulation model — non-visual, but its upcharge is in the total, so name it here too.
    const construction = sel.constructionId ? collection?.constructions.find((x) => x.id === sel.constructionId) : undefined;
    return [
      design && { label: "Panel", name: design.name, imageUrl: design.imageUrl },
      color && { label: "Color", name: color.name, imageUrl: color.imageUrl },
      win && { label: "Window", name: win.name, imageUrl: win.imageUrl },
      hardware && { label: "Hardware", name: hardware.name, imageUrl: hardware.imageUrl },
      construction && { label: "Insulation", name: construction.name, imageUrl: construction.imageUrl },
    ].filter(Boolean) as { label: string; name: string; imageUrl: string | null }[];
  }

  // Per-door payload — the server re-prices each from its rules; we only send the selection + size.
  function doorPayloads() {
    return doors.map((d) => {
      const sel = d.selection;
      const color = collection?.colors.find((c) => c.id === sel.colorId);
      const win = collection?.windows.find((w) => w.id === sel.windowOptionId);
      const dims = sizeDims(effectiveSize(d));
      return {
        doorIndex: d.index,
        collectionId: doc.collectionId,
        designId: sel.designId ?? null,
        colorId: sel.colorId ?? null,
        colorGroup: color?.family ?? null,
        windowOptionId: sel.windowOptionId ?? null,
        windowGroup: win?.placement ?? null,
        // Pre-glass persisted sessions rehydrate without the field — normalize undefined to null.
        glassTypeId: sel.glassTypeId ?? null,
        hardwareId: sel.hardwareId ?? null,
        // Pre-insulation-feature sessions rehydrate without the field — normalize undefined to null.
        constructionId: sel.constructionId ?? null,
        widthFt: dims.width,
        heightFt: dims.height,
      };
    });
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).some((k) => errs[k as keyof typeof errs])) return;
    setBusy(true);
    setError(null);
    const parts = form.fullName.trim().split(" ");
    const firstName = parts.shift() ?? "";
    const payloads = doorPayloads();
    const primary = payloads[0];
    try {
      if (postcard) {
        await api.submitPostcardQuote(tenantSlug, postcard.code, {
          doors: payloads,
          phone: form.phone,
          email: form.email.trim() || null,
          consentText: consentSentence,
        });
        setDone(true);
        return;
      }
      await api.submitLead(tenantSlug, {
        firstName,
        lastName: parts.join(" "),
        email: form.email,
        phone: form.phone,
        address: form.address,
        renderedImageUrl: display,
        photoUrl: doc.photoUrl,
        collectionId: doc.collectionId,
        designId: primary?.designId ?? null,
        colorId: primary?.colorId ?? null,
        colorGroup: primary?.colorGroup ?? null,
        windowOptionId: primary?.windowOptionId ?? null,
        windowGroup: primary?.windowGroup ?? null,
        glassTypeId: primary?.glassTypeId ?? null,
        hardwareId: primary?.hardwareId ?? null,
        constructionId: primary?.constructionId ?? null,
        widthFt: primary?.widthFt ?? null,
        heightFt: primary?.heightFt ?? null,
        doors: payloads,
        consentText: consentSentence,
      }, doc.sessionId);
      // Only confirm once the lead is actually recorded — never show a false "Thanks!".
      setDone(true);
    } catch {
      setError("Sorry, we couldn't send that just now. Please try again.");
    } finally {
      setBusy(false);
    }
  }

  if (!doc.photoUrl || leadCaptureDisabled) return null;
  const preview = display ?? doc.photoUrl;

  return (
    <AppShell step="schedule">
      {done ? (
        <div style={{ ...card, textAlign: "center", maxWidth: 460, margin: "0 auto" }}>
          {branding?.logoUrl && <img src={branding.logoUrl} alt="" style={{ height: 36, objectFit: "contain", marginBottom: 12 }} />}
          <div style={{ width: 56, height: 56, borderRadius: "50%", background: "var(--brand-tint)", color: "var(--brand)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 10px" }}>
            <CheckIcon size={28} />
          </div>
          <h2 style={{ margin: "0 0 6px" }}>Thanks!</h2>
          {/* Names WHO calls and WHY — a homeowner who expects the call answers it. */}
          <p style={{ color: "var(--muted)", marginTop: 0 }}>
            {texts?.successText
              ?? (branding?.name
                ? `${branding.name} will contact you shortly to confirm your price.`
                : "We'll contact you shortly to confirm your price.")}
          </p>
          <Button variant="secondary" onClick={() => { resetAll(); navigate(tenantPath(tenantSlug)); }}>
            Start over
          </Button>
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: stacked ? "1fr" : "1fr 1fr", gap: 16, alignItems: "start" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={card}>
              <img src={preview} alt="" style={{ width: "100%", maxHeight: 220, objectFit: "cover", borderRadius: 8, display: "block" }} />
            </div>

            {doors.length === 0 ? (
              <div style={card}>
                <div style={{ fontSize: 14, color: "var(--muted)", marginBottom: 10 }}>No door configured yet.</div>
                <Button variant="secondary" onClick={() => navigate(tenantPath(tenantSlug, "visualize"))}>Back to styles</Button>
              </div>
            ) : (
              <>
                {doors.map((d) => {
                  const size = effectiveSize(d);
                  const dims = sizeDims(size);
                  const qe = quotes[d.id];
                  const chips = doorChips(d);
                  const names = resolveDoorNames(collection, d.selection);
                  const heading = doors.length === 1 && names.length > 0
                    ? [collection?.name, names[0]].filter(Boolean).join(" ")
                    : `Door ${d.index + 1}`;
                  return (
                    <div key={d.id} style={card}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10, justifyContent: "space-between" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                          <span style={indexPill}>{d.index + 1}</span>
                          <span style={{ fontSize: 15, fontWeight: 700, color: "var(--text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{heading}</span>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
                          <span
                            title={size === "single" ? "Single-car door (8×7)" : "Double-car door (16×7)"}
                            style={{ padding: "3px 10px", borderRadius: "var(--radius-pill)", fontSize: 11, fontWeight: 600, background: "var(--brand-tint)", color: "var(--brand-active)" }}
                          >
                            {sizeLabel(size)} {dims.width}×{dims.height}
                          </span>
                          <button
                            type="button"
                            onClick={() => editDoor(d.id)}
                            style={{ padding: "4px 12px", borderRadius: "var(--radius-pill)", fontSize: 12, fontWeight: 600, cursor: "pointer", border: "1px solid var(--border-strong)", background: "var(--surface)", color: "var(--brand-active)" }}
                          >
                            Edit
                          </button>
                        </div>
                      </div>

                      {chips.length > 0 && (
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: 10, marginTop: 12 }}>
                          {chips.map((c, i) => (
                            <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: 8, border: "1px solid var(--border)", borderRadius: "var(--radius-sm)", background: "var(--bg)" }}>
                              {c.imageUrl ? (
                                <img src={c.imageUrl} alt="" style={{ width: 40, height: 40, flexShrink: 0, borderRadius: 6, border: "1px solid var(--border)", objectFit: "cover", display: "block" }} />
                              ) : (
                                <div style={{ width: 40, height: 40, flexShrink: 0, borderRadius: 6, border: "1px solid var(--border)", background: "var(--surface)", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--muted-2)", fontSize: 13, fontWeight: 600 }}>{c.name.slice(0, 1)}</div>
                              )}
                              <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
                                <span style={{ fontSize: 11, color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".02em" }}>{c.label}</span>
                                <span style={{ fontSize: 13, fontWeight: 600, color: "var(--text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.name}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {pricingEnabled && (
                        <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid var(--border)" }}>
                          {qe?.loading && !qe.quote ? (
                            <div style={{ fontSize: 13, color: "var(--muted-2)" }}>Calculating price…</div>
                          ) : qe?.quote ? (
                            <PriceLine
                              price={qe.quote}
                              isSingle={size === "single"}
                              subtext={`${dims.width}×${dims.height} installed`}
                              financingEnabled={branding?.financingEnabled}
                              financingTermMonths={branding?.financingTermMonths}
                              financingApr={branding?.financingApr}
                            />
                          ) : (
                            <div>
                              <div style={{ fontSize: 13, fontWeight: 600, color: "var(--muted)" }}>Starting from</div>
                              <div style={{ fontSize: 15, fontWeight: 600, color: "var(--muted)", marginTop: 2 }}>Pricing on request</div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}

                {pricingEnabled && doors.length > 1 && totals?.anyPrice && (
                  <div style={{ ...card, background: "var(--brand-tint)", border: "1px solid var(--brand)" }}>
                    <div style={{ fontSize: 13, color: "var(--muted)", marginBottom: 2 }}>Estimated total ({doors.length} doors)</div>
                    <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
                      <span style={{ fontSize: 13, fontWeight: 600, color: "var(--muted)" }}>Starting from</span>
                      <span style={{ fontSize: 24, fontWeight: 700, color: "var(--text)" }}>{dollars(totals.price)}</span>
                      {branding?.financingEnabled && totals.anyMonthly && (
                        <span style={{ fontSize: 14, fontWeight: 600, color: "var(--muted)" }}>· {dollarsCents(totals.monthly)}/mo</span>
                      )}
                    </div>
                  </div>
                )}
                {pricingEnabled && doors.length > 1 && !allLoaded && (
                  <div style={{ fontSize: 12, color: "var(--muted-2)" }}>Calculating total…</div>
                )}
              </>
            )}
          </div>

          <form style={card} onSubmit={submit} noValidate>
            <h2 style={{ marginTop: 0, marginBottom: 4 }}>{texts?.formHeading ?? "Get a free quote"}</h2>
            {/* Pre-sells the measurement visit so the dealer's call is expected, not a surprise —
                and "final price" resolves the tension with the on-screen estimate. */}
            <p style={{ color: "var(--muted)", fontSize: 13, margin: "0 0 12px" }}>
              {texts?.formSubtext
                ?? (branding?.name
                  ? `Free, no obligation — ${branding.name} confirms your final price with a quick measurement.`
                  : "Free, no obligation — your dealer confirms your final price with a quick measurement.")}
            </p>
            {postcard ? (
              <p style={{ color: "var(--muted)", fontSize: 13, marginTop: 0 }}>
                We have your address from your postcard — just tell us the best number to reach you.
              </p>
            ) : (
              <>
                <Field label="Full name" value={form.fullName} onChange={(v) => update("fullName", v)} required error={errors.fullName} testId="full-name" />
                <label style={{ display: "block", marginBottom: 10 }}>
                  <span style={{ display: "block", fontSize: 13, color: "var(--muted)", marginBottom: 4 }}>
                    Address<span aria-hidden="true" style={{ color: "var(--danger)" }}> *</span>
                  </span>
                  <AddressAutocomplete value={form.address} onChange={(v) => update("address", v)} required testId="address" />
                  {errors.address && <span role="alert" data-testid="address-error" style={fieldErrorStyle}>{errors.address}</span>}
                </label>
              </>
            )}
            <Field label="Phone" type="tel" value={form.phone} onChange={(v) => update("phone", formatUsPhone(v))} required error={errors.phone} testId="phone" inputMode="tel" />
            <Field label="Email (optional)" type="email" value={form.email} onChange={(v) => update("email", v)} error={errors.email} testId="email" inputMode="email" />
            {error && (
              <p role="alert" style={{ color: "var(--danger, #dc2626)", fontSize: 13, margin: "4px 0 0" }}>{error}</p>
            )}
            <Button type="submit" variant="primary" block disabled={busy} style={{ marginTop: 8 }}>
              {busy ? "Sending…" : (texts?.submitButton ?? "Request my quote")}
            </Button>
            {/* Inquiry-consent disclosure at the highest-drop step: names the dealer, names the
                channels, sets expectations — and the EXACT sentence rendered here is submitted with
                the lead (consentText) as the dealer's auditable record of what was agreed to. */}
            <p style={{ fontSize: 12, color: "var(--muted)", margin: "10px 0 0", textAlign: "center", lineHeight: 1.4 }}>
              {consentSentence}
            </p>
          </form>
        </div>
      )}
    </AppShell>
  );
}

const indexPill: React.CSSProperties = {
  flexShrink: 0,
  minWidth: 24,
  height: 24,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "0 7px",
  borderRadius: "var(--radius-pill)",
  fontSize: 12,
  fontWeight: 700,
  background: "var(--brand)",
  color: "var(--on-brand)",
};

const fieldErrorStyle: React.CSSProperties = { display: "block", color: "var(--danger)", fontSize: 12, marginTop: 4 };

function Field({
  label, value, onChange, type = "text", required, error, testId, inputMode,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  required?: boolean;
  error?: string;
  testId?: string;
  inputMode?: React.HTMLAttributes<HTMLInputElement>["inputMode"];
}) {
  return (
    <label style={{ display: "block", marginBottom: 10 }}>
      <span style={{ display: "block", fontSize: 13, color: "var(--muted)", marginBottom: 4 }}>
        {label}{required && <span aria-hidden="true" style={{ color: "var(--danger)" }}> *</span>}
      </span>
      <input
        className="rg-input"
        type={type}
        value={value}
        required={required}
        aria-required={required || undefined}
        inputMode={inputMode}
        data-testid={testId}
        onChange={(e) => onChange(e.target.value)}
      />
      {error && <span role="alert" data-testid={testId ? `${testId}-error` : undefined} style={fieldErrorStyle}>{error}</span>}
    </label>
  );
}
